import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, User, Calendar, Phone, Mail, Tag } from 'lucide-react';
import { crmAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STAGES = ['new', 'contacted', 'interested', 'qualified', 'proposal_sent', 'booked', 'lost'];
const STAGE_COLORS = {
  new: '#6B7280', contacted: '#2563EB', interested: '#7C3AED',
  qualified: '#D97706', proposal_sent: '#0891B2', booked: '#059669', lost: '#DC2626'
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newActivity, setNewActivity] = useState({ type: 'note_added', description: '' });
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editStage, setEditStage] = useState('');

  const fetchLead = async () => {
    try {
      const res = await crmAPI.getOne(id);
      setLead(res.data.data);
      setEditStage(res.data.data.stage);
    } catch {
      toast.error('Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLead(); }, [id]);

  const handleStageChange = async (newStage) => {
    try {
      await crmAPI.updateStage(id, { stage: newStage });
      setEditStage(newStage);
      await fetchLead();
      toast.success('Stage updated');
    } catch {
      toast.error('Update failed');
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.description.trim()) return;
    setSaving(true);
    try {
      await crmAPI.addActivity(id, newActivity);
      setNewActivity({ type: 'note_added', description: '' });
      setShowActivityForm(false);
      await fetchLead();
      toast.success('Activity added');
    } catch {
      toast.error('Failed to add activity');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldUpdate = async (field, value) => {
    try {
      await crmAPI.update(id, { [field]: value });
      await fetchLead();
      toast.success('Updated');
    } catch {
      toast.error('Update failed');
    }
  };

  const handleFollowUpDate = async (dateString) => {
    if (!dateString) {
      // Clear follow-up date
      await handleFieldUpdate('followUpDate', null);
      return;
    }
    
    const followUpDate = new Date(dateString);
    await handleFieldUpdate('followUpDate', followUpDate);
  };

  const handleScheduleFollowUp = async () => {
    const followUpDateInput = document.querySelector('input[type="datetime-local"]');
    const followUpNotes = document.getElementById('followup-notes').value;
    
    if (!followUpDateInput.value) {
      toast.error('Please select a follow-up date');
      return;
    }
    
    if (!followUpNotes.trim()) {
      toast.error('Please add follow-up notes');
      return;
    }
    
    setSaving(true);
    try {
      // Update follow-up date
      const followUpDate = new Date(followUpDateInput.value);
      await crmAPI.update(id, { 
        followUpDate,
        lastContactedAt: new Date()
      });
      
      // Add activity for follow-up
      await crmAPI.addActivity(id, {
        type: 'note_added',
        description: `Follow-up scheduled: ${followUpNotes}`,
        performedBy: user._id
      });
      
      // Clear the form
      followUpDateInput.value = '';
      document.getElementById('followup-notes').value = '';
      
      await fetchLead();
      toast.success('Follow-up scheduled successfully');
    } catch {
      toast.error('Failed to schedule follow-up');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!lead) return <div className="empty-state"><h3>Lead not found</h3></div>;

  const ACTIVITY_ICONS = {
    email_sent: '📧', sms_sent: '💬', call_made: '📞',
    note_added: '📝', stage_changed: '🔄', property_viewed: '🏠', booking_attempted: '🛒'
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/crm')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{lead.firstName} {lead.lastName}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span className="badge" style={{ color: STAGE_COLORS[lead.stage], background: `${STAGE_COLORS[lead.stage]}15` }}>
                {lead.stage?.replace('_', ' ')}
              </span>
              <span className="badge badge-gray">{lead.source?.replace('_', ' ')}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowActivityForm(p => !p)}>
            <Plus size={16} /> Log Activity
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        {/* Left: Activity Timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Contact Info */}
          <div className="card">
            <div className="card-header"><span className="card-title">Contact Information</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { icon: Mail, label: 'Email', value: lead.email },
                { icon: Phone, label: 'Phone', value: lead.phone || '—' },
                { icon: Calendar, label: 'Check-in', value: lead.travelDates?.checkIn ? (() => { try { return format(new Date(lead.travelDates.checkIn), 'MMM d, yyyy'); } catch { return '—'; } })() : '—' },
                { icon: Calendar, label: 'Check-out', value: lead.travelDates?.checkOut ? (() => { try { return format(new Date(lead.travelDates.checkOut), 'MMM d, yyyy'); } catch { return '—'; } })() : '—' },
                { icon: User, label: 'Guests', value: lead.guestCount || '—' },
                { icon: Tag, label: 'Budget', value: lead.budget?.max ? `$${lead.budget.min || 0}–$${lead.budget.max}` : '—' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Icon size={16} color="#9CA3AF" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                    <div style={{ fontSize: 14, color: '#111827', marginTop: 2 }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
            {lead.message && (
              <div style={{ marginTop: 16, padding: '12px', background: '#F9FAFB', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6, textTransform: 'uppercase' }}>Initial Message</div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{lead.message}</p>
              </div>
            )}
          </div>

          {/* Add Activity Form */}
          {showActivityForm && (
            <div className="card" style={{ border: '2px solid #3B82F6' }}>
              <div className="card-header"><span className="card-title">Log Activity</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Activity Type</label>
                  <select className="form-select" value={newActivity.type} onChange={e => setNewActivity(p => ({ ...p, type: e.target.value }))}>
                    {[
                      ['email_sent', '📧 Email Sent'],
                      ['sms_sent', '💬 SMS Sent'],
                      ['call_made', '📞 Call Made'],
                      ['note_added', '📝 Note Added'],
                    ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes *</label>
                  <textarea className="form-textarea" rows={3} placeholder="Describe the activity or add notes..."
                    value={newActivity.description} onChange={e => setNewActivity(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setShowActivityForm(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleAddActivity} disabled={saving || !newActivity.description.trim()}>
                    {saving ? 'Saving...' : 'Log Activity'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="card">
            <div className="card-header"><span className="card-title">Activity Timeline</span></div>
            {(lead.activities || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>No activities yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[...lead.activities].reverse().map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: 16, paddingTop: i === 0 ? 0 : 16, borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      {ACTIVITY_ICONS[a.type] || '📌'}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', textTransform: 'capitalize' }}>
                        {a.type?.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{a.description}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                        {a.createdAt ? (() => { try { return format(new Date(a.createdAt), 'MMM d, yyyy h:mm a'); } catch { return ''; } })() : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Stage + Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Pipeline Stage */}
          <div className="card">
            <div className="card-header"><span className="card-title">Pipeline Stage</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {STAGES.map(stage => (
                <button key={stage}
                  onClick={() => handleStageChange(stage)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: editStage === stage ? `${STAGE_COLORS[stage]}15` : 'transparent',
                    textAlign: 'left', width: '100%',
                    fontWeight: editStage === stage ? 600 : 400,
                    color: editStage === stage ? STAGE_COLORS[stage] : '#6B7280',
                    fontSize: 14
                  }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_COLORS[stage], flexShrink: 0 }} />
                  <span style={{ textTransform: 'capitalize' }}>{stage.replace('_', ' ')}</span>
                  {editStage === stage && <span style={{ marginLeft: 'auto', fontSize: 12 }}>✓</span>}
                </button>
              ))}
            </div>
          </div>

         

          {/* Interested Properties */}
          {lead.interestedProperties?.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Interested Properties</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lead.interestedProperties.map(p => (
                  <div key={p._id} onClick={() => navigate(`/properties/${p._id}`)}
                    style={{ padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                    🏠 {p.title}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-up */}
          <div className="card">
            <div className="card-header"><span className="card-title">Follow-up</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Follow-up Date</label>
                <input 
                  type="datetime-local" 
                  className="form-input" 
                  defaultValue={lead.followUpDate ? (() => {
                    try {
                      const date = new Date(lead.followUpDate);
                      return date.toISOString().slice(0, 16);
                    } catch { return ''; }
                  })() : ''}
                  onChange={e => handleFollowUpDate(e.target.value)}
                />
              </div>
              {lead.followUpDate && (
                <div style={{ padding: '8px 12px', background: '#FEF3C7', borderRadius: 6, fontSize: 13, color: '#92400E' }}>
                  📅 Follow-up scheduled: {(() => {
                    try {
                      const date = new Date(lead.followUpDate);
                      return format(date, 'MMM d, yyyy h:mm a');
                    } catch { return 'Invalid date'; }
                  })()}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Follow-up Notes</label>
                <textarea 
                  className="form-textarea" 
                  rows={3} 
                  placeholder="What needs to be discussed or done..."
                  id="followup-notes"
                />
              </div>
              <button 
                className="btn btn-primary btn-sm" 
                onClick={handleScheduleFollowUp}
                style={{ alignSelf: 'flex-start' }}
              >
                Schedule Follow-up
              </button>
            </div>
          </div>

          {/* Meta */}
          <div className="card">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#9CA3AF' }}>Created</span>
                <span>{(() => { try { return format(new Date(lead.createdAt), 'MMM d, yyyy'); } catch { return '—'; } })()}</span>
              </div>
              {lead.lastContactedAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#9CA3AF' }}>Last Contact</span>
                  <span>{(() => { try { return format(new Date(lead.lastContactedAt), 'MMM d, yyyy'); } catch { return '—'; } })()}</span>
                </div>
              )}
              {lead.followUpDate && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#059669' }}>
                  <span style={{ color: '#059669' }}>Follow-up</span>
                  <span>{(() => { try { return format(new Date(lead.followUpDate), 'MMM d, yyyy h:mm a'); } catch { return '—'; } })()}</span>
                </div>
              )}
              {lead.convertedToBooking && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: '#ECFDF5', borderRadius: 8, color: '#059669', fontWeight: 500, fontSize: 14 }}>
                  ✅ Converted to booking
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailPage;
